import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { PlayCircle, Pause, CheckCircle, BarChart3, Clock } from "lucide-react";

/**
 * Displays all in-progress / paused interview sessions so the user can
 * pick up where they left off. Rendered at the top of the AI Interview
 * Buddy setup screen.
 */
export const ResumableInterviewsCard = ({ sessions, onResume, loading = false }) => {
  if (!sessions || sessions.length === 0) return null;

  return (
    <Card
      className="mb-6 border-0 shadow-lg bg-gradient-to-br from-amber-50 to-orange-50 border-l-4 border-l-amber-500"
      data-testid="resumable-sessions-card"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <PlayCircle className="w-5 h-5 text-amber-600" />
            Continue Where You Left Off
          </CardTitle>
          <Badge variant="outline" className="bg-white">
            {sessions.length} interview{sessions.length > 1 ? "s" : ""} in progress
          </Badge>
        </div>
        <CardDescription className="text-amber-900/70">
          You have unfinished interviews. Resume from exactly where you stopped.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {sessions.slice(0, 3).map((rs) => (
          <ResumableSessionRow
            key={rs.id}
            session={rs}
            onResume={onResume}
            loading={loading}
          />
        ))}
        {sessions.length > 3 && (
          <p className="text-xs text-center text-gray-600 pt-1">
            + {sessions.length - 3} more in-progress interviews
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const ResumableSessionRow = ({ session, onResume, loading }) => (
  <div
    className="flex items-center justify-between gap-3 p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
    data-testid={`resumable-session-${session.id}`}
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <span className="font-semibold text-sm truncate">
          {session.resume_name ? `${session.resume_name} — ` : ""}
          {session.target_role || session.career_field || "Interview"}
        </span>
        <Badge className="text-xs bg-purple-100 text-purple-800">
          {session.type === "resume_based" ? "📄 Resume-Based" : `🎯 ${session.mode}`}
        </Badge>
        {session.is_paused && (
          <Badge className="text-xs bg-amber-100 text-amber-800">
            <Pause className="w-3 h-3 mr-1" />
            Paused
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-3 text-xs text-gray-600">
        <span className="flex items-center gap-1">
          <CheckCircle className="w-3 h-3 text-green-600" />
          {session.questions_answered}/{session.total_questions} answered
        </span>
        <span className="flex items-center gap-1">
          <BarChart3 className="w-3 h-3 text-blue-600" />
          Score: {session.running_score || 0}/100
        </span>
        {session.last_paused_at && (
          <span className="flex items-center gap-1 text-gray-500">
            <Clock className="w-3 h-3" />
            {new Date(session.last_paused_at).toLocaleDateString()}
          </span>
        )}
      </div>
      <Progress value={session.progress_percent} className="h-1.5 mt-2" />
    </div>
    <Button
      size="sm"
      onClick={() => onResume(session.id)}
      disabled={loading}
      className="bg-amber-500 hover:bg-amber-600 text-white shrink-0"
      data-testid={`resume-btn-${session.id}`}
    >
      <PlayCircle className="w-4 h-4 mr-1" />
      Resume
    </Button>
  </div>
);

export default ResumableInterviewsCard;
